
import React, { useState } from 'react';
import { useDataRoom } from '../../context/DataRoomContext';
import { Document, DocumentType } from '../../types/dataRoom';
import { downloadDocuments, getDocumentCategory, formatDocumentType } from '../../utils/documentUtils';

// Import our components
import DocumentHeader from './DocumentHeader';
import EmptyStateDocuments from './EmptyStateDocuments';
import DocumentTableHeader from './DocumentTableHeader';
import DocumentTreeItem from './DocumentTreeItem';
import DocumentDialogs from './DocumentDialogs';
import DocumentCategoryTags from './DocumentCategoryTags';
import { useDocumentActions } from '../../hooks/useDocumentActions';

interface DocumentSubCategory {
  type: DocumentType;
  label: string;
  documents: Document[];
}

const DocumentList: React.FC = () => {
  const { documents, removeDocument, toggleDocumentSelection } = useDataRoom();

  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [expandedSubCategories, setExpandedSubCategories] = useState<Record<string, boolean>>({});

  // Document action hooks
  const {
    isEditTagsOpen,
    setIsEditTagsOpen,
    isEditTypeOpen,
    setIsEditTypeOpen,
    currentTags,
    setCurrentTags,
    currentType,
    setCurrentType,
    isViewDocumentOpen,
    viewDocumentUrl,
    viewDocumentName,
    isDownloading,
    handleDownloadSelected,
    handleDownloadAll,
    handleSelectAll,
    openEditTags,
    openEditType,
    handleSaveTags,
    handleSaveType,
    handleViewDocument,
    closeViewDocument,
  } = useDocumentActions();
  
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleSubCategory = (subCategory: string) => {
    setExpandedSubCategories(prev => ({
      ...prev,
      [subCategory]: !prev[subCategory]
    }));
  };

  // Group documents by category and then by document type
  const groupedDocuments = documents.reduce((acc, doc) => {
    const category = getDocumentCategory(doc.type);
    
    if (!acc[category]) {
      acc[category] = {} as Record<DocumentType, Document[]>;
    }
    
    if (!acc[category][doc.type]) {
      acc[category][doc.type] = [];
    }
    
    acc[category][doc.type]?.push(doc);
    return acc;
  }, {} as Record<string, Partial<Record<DocumentType, Document[]>>>);

  // Convert grouped documents to tree structure
  const documentTree = Object.entries(groupedDocuments).map(([category, typeGroups]) => {
    const subCategories: DocumentSubCategory[] = Object.entries(typeGroups || {}).map(([type, docs]) => ({
      type: type as DocumentType,
      label: formatDocumentType(type as DocumentType),
      documents: docs || []
    }));

    return {
      category,
      subCategories
    };
  }).sort((a, b) => a.category.localeCompare(b.category));
  
  // Document type options for the dropdown
  const documentTypeOptions: DocumentType[] = [
    'audited_financials_2022_23',
    'audited_financials_2023_24',
    'audited_financials_2024_25',
    'provisional_financials',
    'mis_financials',
    'bank_statements',
    'gst_filing',
    'company_kyc',
    'director_kyc',
    'company_deck',
    'itr',
    'sanction_letter',
    'asset_leasing',
    'other'
  ];
  
  return (
    <div className="space-y-4">
      {/* Header section with title and download buttons */}
      <DocumentHeader 
        selectedDocumentsCount={documents.filter(doc => doc.isSelected).length}
        handleDownloadSelected={handleDownloadSelected}
        handleDownloadAll={handleDownloadAll}
        isDownloading={isDownloading}
      />
      
      {/* Category tags above the table */}
      <DocumentCategoryTags documents={documents} />
      
      {/* Empty states */}
      <EmptyStateDocuments 
        isFilteredEmpty={false}
        hasDocuments={documents.length > 0}
      />
      
      {/* Document tree */}
      {documents.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          {/* Table header */}
          <DocumentTableHeader 
            handleSelectAll={handleSelectAll}
            selectedDocumentsCount={documents.filter(doc => doc.isSelected).length}
            filteredDocumentsLength={documents.length}
          />
          
          {/* Document tree structure */}
          <div className="divide-y">
            {documentTree.map(({ category, subCategories }) => (
              <DocumentTreeItem
                key={category}
                category={category}
                subCategories={subCategories}
                isExpanded={!!expandedCategories[category]}
                expandedSubCategories={expandedSubCategories}
                toggleCategory={toggleCategory}
                toggleSubCategory={toggleSubCategory}
                toggleDocumentSelection={toggleDocumentSelection}
                openEditTags={openEditTags}
                openEditType={openEditType}
                handleViewDocument={handleViewDocument}
                downloadDocuments={downloadDocuments}
                removeDocument={removeDocument}
              />
            ))}
          </div>
        </div>
      )}

      {/* All dialogs */}
      <DocumentDialogs
        isEditTagsOpen={isEditTagsOpen}
        setIsEditTagsOpen={setIsEditTagsOpen}
        currentTags={currentTags}
        setCurrentTags={setCurrentTags}
        handleSaveTags={handleSaveTags}
        
        isEditTypeOpen={isEditTypeOpen}
        setIsEditTypeOpen={setIsEditTypeOpen}
        currentType={currentType}
        setCurrentType={setCurrentType}
        handleSaveType={handleSaveType}
        documentTypeOptions={documentTypeOptions}
        
        isViewDocumentOpen={isViewDocumentOpen}
        viewDocumentUrl={viewDocumentUrl}
        viewDocumentName={viewDocumentName}
        closeViewDocument={closeViewDocument}
      />
    </div>
  );
};

export default DocumentList;
